
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GlobeAltIcon, MicrophoneIcon, UserGroupIcon, } from '@heroicons/react/outline';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect } from 'react';
import { Product, Voice } from 'types';
import { getActiveCatalogVoices } from '@/data/catalog_voices';
import { getProductsWithPricesInArray } from '@/data/products';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import PricingForLandingPage from '@/components/PricingForLandingPage';
import Seo from '@/components/Seo';
import TTSLandingForm from '@/components/speech/TTSLandingForm';
import VoiceSelection, { TTS_LANDING_CONTEXT_KEY, } from '@/components/speech/VoicesForLandingPage';
import Testimonials from '@/components/Testimonials';
import ActingVoicesSection, { VOICES_THAT_ACT_ANCHOR, } from '@/components/TextToSpeech/ActingVoicesSection';
import HowToCreateSection from '@/components/TextToSpeech/HowToCreateSection';
import TalkingAvatarsSection from '@/components/TextToSpeech/TalkingAvatarsSection';
import VoiceDesignSection from '@/components/TextToSpeech/VoiceDesignSection';
import TextToSpeechLanguages, { languages, } from '@/components/TextToSpeechLanguages';
import TTSLanguageFaq from '@/components/TTSLanguageFaq';
import { tts_product_ids } from '@/constants/common';
interface Props {
    products: Product[];
    voices: Voice[];
}
const TextToSpeechLanguage = ({ products, voices }: Props) => {
    const { t, lang } = useLocale('text-to-speech');
    const router = useRouter();
    const { language } = router.query;
    // Find the language object
    const languageObj = typeof language === 'string'
        ? languages.find((lang) => (lang.slug || lang.name.toLowerCase().replace(' ', '-')) ===
            language.toLowerCase())
        : undefined;
    // Save language/accent context so the TTS page can preselect filters.
    // Style pages (funny, scary, ...) don't map to a language/accent filter,
    // so they CLEAR any context left by a previously visited language page
    // (otherwise /tts would apply that stale filter) and only carry over the
    // selected voice (handled in TTSLandingForm).
    useEffect(() => {
        if (!languageObj)
            return;
        if (languageObj.isStyle) {
            localStorage.removeItem(TTS_LANDING_CONTEXT_KEY);
            return;
        }
        const context = languageObj.isAccent
            ? { accent: languageObj.name }
            : { language: languageObj.name };
        localStorage.setItem(TTS_LANDING_CONTEXT_KEY, JSON.stringify(context));
    }, [languageObj]);
    if (typeof language !== 'string' || !languageObj) {
        return null;
    }
    return (<main className='relative bg-gray-100 dark:bg-slate-900'>
      {/* <Banner /> Black Friday is over */}
      <Seo templateTitle={languageObj.isStyle
            ? t(languageObj.name) +
                ' ' +
                t('Voice Generator') +
                ' | ' +
                t(languageObj.name) +
                ' ' +
                t('Text to Speech Online')
            : t(languageObj.name) +
                ' ' +
                (languageObj.isAccent ? t('Accent') + ' ' : ' ') +
                t('Text-to-Speech') +
                ' | ' +
                t('AI Voice Generator Online')} description={t('Generate high-quality spoken audio in any voice, style, and language. Our text reader is powered by an AI model that renders human intonation and inflections with unrivaled fidelity, adjusting the delivery based on context.')} image={`https://easy-peasy.ai/api/og-template?title=${t(languageObj.name) + t('AI Text to Speech')}`}/>
      <div className='relative isolate overflow-hidden bg-white dark:bg-slate-900'>
        <LandingPageNavigation />

        <div className='absolute inset-0 -z-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]'></div>

        <div className='absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]' aria-hidden='true'>
          <div className='aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-20' style={{
            clipPath: 'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
        }}></div>
        </div>

        <div className='mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:pb-32 2xl:max-w-[1440px]'>
          <div className='mx-auto max-w-2xl lg:mx-0 lg:w-[55%] lg:max-w-none lg:flex-shrink-0 2xl:w-[50%]'>
            <div className='mb-6'>
              <a href={`#${VOICES_THAT_ACT_ANCHOR}`} className='group inline-flex items-center gap-2 rounded-full bg-primary-500/10 py-1 pl-1 pr-3 text-sm font-medium leading-6 text-primary-700 ring-1 ring-inset ring-primary-500/20 transition-colors hover:bg-primary-500/15 dark:text-primary-300'>
                <span className='rounded-full bg-primary-600 px-2 text-xs font-semibold leading-5 text-white dark:bg-primary-500'>
                  {t('New')}
                </span>
                <span>{t('Voices that laugh, whisper and sigh')}</span>
                <span aria-hidden='true' className='transition-transform duration-200 ease-out group-hover:translate-y-0.5'>
                  &darr;
                </span>
              </a>
            </div>
            <h1 className='text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-[2.75rem] lg:leading-[1.15] xl:text-5xl'>
              {t(languageObj.name)}{' '}
              {languageObj.isAccent && (<span className='text-gray-900 dark:text-white'>
                  {t('Accent')}{' '}
                </span>)}
              <span className='text-gray-900 dark:text-white'>
                {t('Text to Speech Voices Online')}
              </span>
            </h1>
            <p className='mt-4 text-base leading-7 text-gray-600 dark:text-slate-300 lg:text-base xl:text-lg xl:leading-8'>
              {t('Our AI solution, trained on a broad and diverse set of languages, offers unprecedented robustness and accuracy in speech synthesis, reaching human-level proficiency. Whether you need text-to-speech services for reading, accessibility, or content creation, our AI has your back.')}
            </p>
            <div className='mt-6 flex flex-wrap gap-2'>
              {[
            { icon: GlobeAltIcon, textKey: '70+ Languages' },
            { icon: UserGroupIcon, textKey: '1,900+ Voices' },
            { icon: MicrophoneIcon, textKey: 'Voice Cloning' },
        ].map((feature, idx) => (<div key={idx} className='flex items-center gap-2 rounded-full bg-white dark:bg-slate-800/50 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 shadow-sm ring-1 ring-gray-200 dark:ring-white/10 hover:ring-primary-500/50 transition-all duration-200'>
                  <feature.icon className='h-4 w-4 text-primary-600'/>
                  <span>{t(feature.textKey)}</span>
                </div>))}
            </div>
          </div>

          <div className='relative mt-12 sm:mx-auto sm:max-w-lg lg:mt-0 lg:w-[45%] lg:flex-shrink-0 2xl:w-[50%]'>
            <div className='relative rounded-2xl bg-gray-900/5 dark:bg-white/5 p-3 ring-1 ring-inset ring-gray-900/10 dark:ring-white/10 lg:rounded-3xl lg:p-4'>
              <div className='relative overflow-hidden rounded-xl shadow-2xl bg-white dark:bg-slate-800 ring-1 ring-black/10'>
                <TTSLandingForm voices={voices} languageName={languageObj.name} isAccent={languageObj.isAccent} isStyle={languageObj.isStyle}/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LogosCloud />
      <VoiceSelection voices={voices} language={language} showFilters={false}/>
      <HowToCreateSection languageName={languageObj.name} isAccent={languageObj.isAccent || false} isStyle={languageObj.isStyle || false} languageFlag={languageObj.flag}/>
      <ActingVoicesSection />
      <VoiceDesignSection />
      <TalkingAvatarsSection languageName={languageObj.name} isAccent={languageObj.isAccent || false} isStyle={languageObj.isStyle || false}/>
      {/* <CloneYourVoice /> */}
      <TextToSpeechLanguages />

      <TTSLanguageFaq languageObj={languageObj}/>
      <div className='bg-[#0B0F19]'>
        <PricingForLandingPage isUpgrade={true} products={products} hideLowerPrices={false} showOnlyTTSFeatures={true}/>
      </div>
      <FeaturedLogos />
      <Testimonials />
      <CallToAction preset='tts'/>
      <Footer />
    </main>);
};
export default TextToSpeechLanguage;
// add static props
async function getStaticProps({ params, }: {
    params: {
        language: string;
    };
}) {
    const products = await getProductsWithPricesInArray(tts_product_ids);
    const language = params.language;
    // Validate if the language exists in our list
    const isValidLanguage = languages.some((lang) => (lang.slug || lang.name.toLowerCase().replace(' ', '-')) ===
        language.toLowerCase());
    if (!isValidLanguage) {
        return {
            notFound: true,
        };
    }
    // Find the language object to get the actual name for voice filtering
    const languageObj = languages.find((lang) => (lang.slug || lang.name.toLowerCase().replace(' ', '-')) ===
        language.toLowerCase());
    const catalog = (await getActiveCatalogVoices()).map(({ status: _status, ...voice }) => voice);
    const voices = [
        ...catalog.filter((voice) => {
            if (!languageObj)
                return false;
            // For voice styles (funny, scary, ...), match by style tag
            if (languageObj.isStyle) {
                return (voice.styles?.some((style) => style.toLowerCase() === languageObj.name.toLowerCase()) ?? false);
            }
            // For accents, match by accent name
            if (languageObj.isAccent) {
                return voice?.accent?.toLowerCase() === languageObj.name.toLowerCase();
            }
            // For languages, match by language name
            return Array.isArray(voice.language)
                ? voice.language.some((lang) => lang.toLowerCase() === languageObj.name.toLowerCase())
                : voice.language?.toLowerCase() === languageObj.name.toLowerCase();
        }),
    ];
    return {
        props: {
            products,
            voices,
        },
        revalidate: 60,
    };
}
// add static paths
export async function getStaticPaths() {
    // Derive from i18n config so newly added locales (e.g. `vi`) are never
    // silently missing — with fallback: false an omitted locale 404s.
    const { locales } = await import('../../../../i18n.json');
    const paths = languages.flatMap((language) => locales.map((locale) => ({
        params: {
            language: language.slug || language.name.toLowerCase().replace(' ', '-'),
        },
        locale,
    })));
    return {
        paths,
        fallback: false,
    };
}

    async function __Next_Translate__getStaticProps__1a12e86ecff__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/text-to-speech/[language]/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ecff__ as getStaticProps }
  